import { useCallback, useEffect, useRef, useState } from "react";
import type { DebugEvent, Status } from "@delegate/shared";
import {
  Settings,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  MessageCircle,
  ScrollText,
  NotebookPen,
  ListChecks,
  CalendarDays,
  Send,
  Volume2,
} from "lucide-react";
import {
  api,
  ApiError,
  getAvatarId,
  setAvatarId,
  type Me,
  type AvatarSummary,
} from "./api.ts";
import { AvatarDetail } from "./AvatarDetail.tsx";
import { TasksSurface } from "./tools/TasksSurface.tsx";
import { CalendarSurface } from "./tools/CalendarSurface.tsx";
import { guardSurfaceResult, guardTool, type EditingSurface } from "./tools/tool-guard.ts";
import type { ToolId } from "../../shared/tools.ts";
import { PortraitCard } from "@/components/brand/portrait-card.tsx";
import { ActivityIndicator } from "@/components/brand/activity-indicator.tsx";
import { Login } from "./Account.tsx";
import { ChangePassword } from "./ChangePassword.tsx";
import { RealtimeSessionClient, type TranscriptEntry } from "./realtime.ts";
import { RepresentativePortrait } from "./RepresentativePortrait.tsx";
import { RepresentativeNotes } from "./RepresentativeNotes.tsx";
import { MarketplaceSurface } from "./Marketplace.tsx";
import { TenantSettings, type TenantSurface } from "./TenantSettings.tsx";
import { WorkPanel } from "./WorkPanel.tsx";
import { useWorkspaceState } from "./useWorkspaceState.ts";
import { primeAvatarAudio } from "./avatar-audio.ts";
import type { RepresentativeActionResult } from "../../shared/representative.ts";
import { ConversationShell } from "@/components/brand/conversation-shell.tsx";
import { WorkSurfaceFrame } from "@/components/brand/work-surface-frame.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Alert, AlertDescription } from "@/components/ui/alert.tsx";
import { Badge } from "@/components/ui/badge.tsx";

const statusLabel: Record<Status, string> = {
  DISCONNECTED: "Ready when you are",
  CONNECTING: "Connecting...",
  LISTENING: "Listening",
  SPEAKING: "Speaking",
  TOOL_RUNNING: "Working",
  ERROR: "Conversation unavailable",
};
const surfaceTitle: Record<TenantSurface, string> = {
  settings: "Settings",
  marketplace: "Employee marketplace",
  notes: "My notes",
  tasks: "Tasks",
  calendar: "Calendar",
  work: "Saved employee work",
  conversation: "Transcript",
};

function RestingRepresentative() {
  const sink = useRef<((event: Record<string, unknown>) => void) | null>(null);
  return (
    <RepresentativePortrait
      stream={null}
      status="DISCONNECTED"
      onReady={() => {}}
      eventSink={sink}
    />
  );
}

export function App() {
  const [details, setDetails] = useState<AvatarSummary | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [booting, setBooting] = useState(true);
  const [activeAvatar, setActiveAvatar] = useState<string | null>(null);
  const [surface, setSurface] = useState<TenantSurface | null>(null);
  const [status, setStatus] = useState<Status>("DISCONNECTED");
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [events, setEvents] = useState<DebugEvent[]>([]);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [portraitReady, setPortraitReady] = useState(false);
  const [muted, setMuted] = useState(false);
  const [textMode, setTextMode] = useState(false);
  const [showText, setShowText] = useState(false);
  const [text, setText] = useState("");
  const [notesVersion, setNotesVersion] = useState(0);
  const [suggestion, setSuggestion] = useState<{
    query?: string;
    templateSlug?: string;
    version: number;
  }>();
  const [sessionUsd, setSessionUsd] = useState(0);
  const clientRef = useRef<RealtimeSessionClient | null>(null);
  const autoStarted = useRef<string | null>(null);
  const [sending, setSending] = useState(false);
  const pendingText = useRef<{
    id: string;
    employee: string;
    text: string;
  } | null>(null);
  // The surface the person is editing by hand; the employee waits for it (tool-guard.ts).
  const editing = useRef<EditingSurface>(null);
  const [heldTools, setHeldTools] = useState<ToolId[]>([]);
  const [tasksVersion, setTasksVersion] = useState(0);
  const [calendarVersion, setCalendarVersion] = useState(0);
  const eventSink = useRef<((event: Record<string, unknown>) => void) | null>(
    null,
  );
  const settingsButton = useRef<HTMLButtonElement>(null);
  const surfaceHost = useRef<HTMLDivElement>(null);
  const loadEpoch = useRef(0);
  const accountId = useRef<string | null>(null);
  const {
    state,
    loading: workLoading,
    error: workError,
    refresh: refreshWork,
  } = useWorkspaceState(activeAvatar);
  const active = me?.avatars.find((a) => a.id === activeAvatar);
  const representative = me?.avatars.find(
    (a) => a.avatarKind === "representative" || a.id === me.defaultAvatarId,
  );
  const isRepresentative =
    !!active &&
    (active.avatarKind === "representative" ||
      active.id === me?.defaultAvatarId);
  const connected = status !== "DISCONNECTED" && status !== "ERROR";

  const stop = useCallback(() => {
    const client = clientRef.current;
    clientRef.current = null;
    client?.stop();
    setStatus("DISCONNECTED");
    setStream(null);
    setMuted(false);
  }, []);
  const clearAuthenticatedState = useCallback(() => {
    loadEpoch.current++;
    stop();
    accountId.current = null;
    autoStarted.current = null;
    editing.current = null;
    setHeldTools([]);
    setAvatarId(null);
    setActiveAvatar(null);
    setMe(null);
    setDetails(null);
    setBooting(false);
    setTranscript([]);
    setEvents([]);
    setSurface(null);
    setSuggestion(undefined);
    setError(null);
    setText("");
    setShowText(false);
    setTextMode(false);
    setPortraitReady(false);
    setNotesVersion(0);
    setSessionUsd(0);
  }, [stop]);
  const loadMe = useCallback(async () => {
    const version = ++loadEpoch.current;
    try {
      const next = await api<Me>("/api/auth/me");
      if (version !== loadEpoch.current) return;
      if (accountId.current && accountId.current !== next.user.id)
        clearAuthenticatedState();
      accountId.current = next.user.id;
      setMe(next);
      const selected = getAvatarId();
      const chosen =
        selected && next.avatars.some((a) => a.id === selected)
          ? selected
          : (next.defaultAvatarId ?? next.avatars[0]?.id ?? null);
      if (chosen !== selected) stop();
      setAvatarId(chosen);
      setActiveAvatar(chosen);
    } catch (err) {
      if (version !== loadEpoch.current) return;
      if (err instanceof ApiError && err.status === 401)
        clearAuthenticatedState();
      else
        setError("Could not load your workspace. Please refresh to try again.");
    } finally {
      if (version === loadEpoch.current) setBooting(false);
    }
  }, [stop, clearAuthenticatedState]);
  useEffect(() => {
    void loadMe();
    return () => {
      loadEpoch.current++;
    };
  }, [loadMe]);
  useEffect(
    () => () => {
      clientRef.current?.stop();
      clientRef.current = null;
    },
    [],
  );

  const openSurface = useCallback(
    (next: TenantSurface, source: "human" | "avatar" = "human") => {
      setSurface(next);
      if (source === "human")
        requestAnimationFrame(() => {
          const heading = surfaceHost.current?.querySelector<HTMLElement>("h2");
          if (heading) {
            heading.tabIndex = -1;
            heading.focus();
          }
        });
    },
    [],
  );
  const closeSurface = () => {
    setSurface(null);
    requestAnimationFrame(() => settingsButton.current?.focus());
  };
  const onSurfaceResult = useCallback(
    (result: RepresentativeActionResult) => {
      if (!result.success) return;
      // The action ran either way: the list behind a refused switch still refreshes.
      if (result.navigation?.surface === "tasks") setTasksVersion((v) => v + 1);
      if (result.navigation?.surface === "calendar")
        setCalendarVersion((v) => v + 1);
      const refused = guardSurfaceResult(result, editing.current);
      if (refused) return refused;
      if (result.navigation) {
        openSurface(result.navigation.surface, "avatar");
        if (result.navigation.surface === "marketplace")
          setSuggestion((previous) => ({
            ...result.navigation,
            version: (previous?.version ?? 0) + 1,
          }));
      }
      if (
        result.navigation?.surface === "notes" ||
        result.notes ||
        result.note ||
        result.facts.some((f) => f.key.toLowerCase().includes("note"))
      )
        setNotesVersion((v) => v + 1);
    },
    [openSurface],
  );
  const onEditingChange = useCallback((id: string | null) => {
    editing.current = id ? "notes" : null;
  }, []);
  const onTasksEditing = useCallback((on: boolean) => {
    editing.current = on ? "tasks" : null;
  }, []);
  const onCalendarEditing = useCallback((on: boolean) => {
    editing.current = on ? "calendar" : null;
  }, []);
  useEffect(() => {
    if (!activeAvatar || !me) return;
    let alive = true;
    api<{ tools: { id: ToolId }[] }>("/api/tools", { avatarId: activeAvatar })
      .then((data) => {
        if (alive) setHeldTools(data.tools.map((t) => t.id));
      })
      .catch(() => {
        if (alive) setHeldTools([]);
      });
    return () => {
      alive = false;
    };
  }, [activeAvatar, me]);

  const start = useCallback(
    async (microphone = true) => {
      if (clientRef.current || !activeAvatar) return;
      if (microphone) primeAvatarAudio();
      setError(null);
      setMuted(false);
      setTextMode(!microphone);
      const isCurrent = () =>
        clientRef.current === client && getAvatarId() === activeAvatar;
      const client = new RealtimeSessionClient({
        onStatus: (value) => {
          if (isCurrent()) {
            setStatus(value);
            if (value === "ERROR" || value === "DISCONNECTED") {
              clientRef.current = null;
              setStream(null);
            }
          }
        },
        onCost: (value) => {
          if (isCurrent()) setSessionUsd(value);
        },
        onDebug: (event) => {
          if (isCurrent()) {
            setEvents((previous) => [...previous.slice(-199), event]);
            if (event.eventType === "TOOL_RESULT") refreshWork();
          }
        },
        onTranscript: (entry) => {
          if (isCurrent())
            setTranscript((previous) => [...previous.slice(-99), entry]);
        },
        onError: (message) => {
          if (isCurrent()) setError(message);
        },
        onAudioStream: (audio) => {
          if (isCurrent()) setStream(audio);
        },
        onRealtimeEvent: (event) => {
          if (isCurrent()) eventSink.current?.(event);
        },
        onSurfaceResult: (result) => {
          if (isCurrent()) return onSurfaceResult(result);
        },
        beforeTool: (name) => guardTool(name, editing.current),
      });
      clientRef.current = client;
      try {
        await client.start({
          microphone,
          sharedConversation: me?.communicationsEnabled,
          greeting: isRepresentative
            ? "Briefly introduce yourself as Destiny, the AI representative. Explain that you can help find an employee, keep a private note, or show the Delegate consultants in Settings. Ask what the user would like to do. Use only verified capabilities; do not invent plans or offers."
            : undefined,
        });
      } catch (err) {
        if (isCurrent()) {
          client.stop();
          clientRef.current = null;
          setStream(null);
          setStatus("ERROR");
          setError(
            err instanceof Error
              ? err.message
              : "Could not connect. Try again or use Settings.",
          );
        }
      }
    },
    [
      activeAvatar,
      isRepresentative,
      onSurfaceResult,
      refreshWork,
      me?.communicationsEnabled,
    ],
  );

  useEffect(() => {
    const hidden = () => {
      if (document.hidden) stop();
    };
    document.addEventListener("visibilitychange", hidden);
    return () => document.removeEventListener("visibilitychange", hidden);
  }, [stop]);
  useEffect(() => {
    if (!connected) return;
    const timeout = setTimeout(
      () => {
        stop();
        setError(
          "This conversation has ended. Start another when you're ready.",
        );
      },
      10 * 60 * 1000,
    );
    const client = clientRef.current;
    const authCheck = setInterval(() => {
      void api<Me>("/api/auth/me")
        .then((next) => {
          if (
            clientRef.current === client &&
            accountId.current !== next.user.id
          )
            clearAuthenticatedState();
        })
        .catch((err) => {
          if (
            clientRef.current === client &&
            err instanceof ApiError &&
            err.status === 401
          )
            clearAuthenticatedState();
        });
    }, 30_000);
    return () => {
      clearTimeout(timeout);
      clearInterval(authCheck);
    };
  }, [connected, stop, clearAuthenticatedState]);

  useEffect(() => {
    if (autoStarted.current !== activeAvatar || !activeAvatar) return;
    autoStarted.current = null;
    void start();
  }, [activeAvatar, start]);
  const switchAvatar = (id: string, mode: "voice" | "text" = "voice") => {
    if (!me?.avatars.some((a) => a.id === id)) return;
    if (id !== activeAvatar) {
      stop();
      autoStarted.current = mode === "voice" ? id : null;
      setShowText(mode === "text");
      setText("");
      setPortraitReady(false);
      setAvatarId(id);
      setActiveAvatar(id);
      setTranscript([]);
      setEvents([]);
      setSessionUsd(0);
      setError(null);
    }
    if (mode === "text") {
      setShowText(true);
      openSurface("conversation");
    } else {
      closeSurface();
      if (id === activeAvatar) void start();
    }
  };
  const signOut = async () => {
    stop();
    setError(null);
    try {
      await api("/api/auth/logout", { body: {} });
      clearAuthenticatedState();
    } catch {
      setError("Could not sign out. Try again.");
    }
  };
  useEffect(() => {
    if (!me?.communicationsEnabled || !activeAvatar || connected) return;
    let alive = true;
    const controller = new AbortController();
    const load = async () => {
      try {
        const messages = await api<
          {
            sender: string;
            text: string;
            createdAt: string;
            workSurface?: "notes" | "tasks" | "calendar";
          }[]
        >(
          `/api/communications/employees/${encodeURIComponent(activeAvatar)}/messages`,
          { signal: controller.signal },
        );
        if (alive)
          setTranscript(
            messages.map((m) => ({
              role: m.sender === "user" ? "user" : "assistant",
              text: m.text,
              timestamp: m.createdAt,
            })),
          );
      } catch (e) {
        if (alive && e instanceof ApiError && e.status === 401)
          clearAuthenticatedState();
      }
    };
    void load();
    const timer = setInterval(() => {
      if (!document.hidden) void load();
    }, 5000);
    return () => {
      alive = false;
      controller.abort();
      clearInterval(timer);
    };
  }, [
    me?.communicationsEnabled,
    activeAvatar,
    connected,
    clearAuthenticatedState,
  ]);
  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    const message = text.trim();
    if (!message || sending) return;
    if (me?.communicationsEnabled && activeAvatar) {
      const employee = activeAvatar,
        epoch = loadEpoch.current;
      if (
        pendingText.current?.employee !== employee ||
        pendingText.current?.text !== message
      )
        pendingText.current = {
          id: crypto.randomUUID(),
          employee,
          text: message,
        };
      const requestId = pendingText.current.id;
      setSending(true);
      setError(null);
      try {
        const messages = await api<
          {
            sender: string;
            text: string;
            createdAt: string;
            workSurface?: "notes" | "tasks" | "calendar";
          }[]
        >(
          `/api/communications/employees/${encodeURIComponent(employee)}/messages`,
          { body: { id: requestId, text: message } },
        );
        if (epoch !== loadEpoch.current || employee !== getAvatarId()) return;
        pendingText.current = null;
        const opened = messages.at(-1)?.workSurface;
        if (opened === "notes") setNotesVersion((v) => v + 1);
        if (opened === "tasks") setTasksVersion((v) => v + 1);
        if (opened === "calendar") setCalendarVersion((v) => v + 1);
        if (opened && !guardSurfaceResult({ success: true, answerMode: "answer_user", facts: [], instructionToRealtime: "", navigation: { surface: opened } }, editing.current))
          openSurface(opened);
        setText("");
        setTranscript(
          messages.map((m) => ({
            role: m.sender === "user" ? "user" : "assistant",
            text: m.text,
            timestamp: m.createdAt,
          })),
        );
      } catch (e) {
        if (epoch === loadEpoch.current && employee === getAvatarId())
          setError(
            e instanceof Error ? e.message : "Message could not be sent.",
          );
      } finally {
        setSending(false);
      }
      return;
    }
    if (!clientRef.current) await start(false);
    try {
      clientRef.current?.sendText(message);
      if (clientRef.current) setText("");
    } catch {
      setError("The conversation ended. Connect again to send your message.");
    }
  };

  if (booting)
    return (
      <main className="grid min-h-dvh place-items-center bg-background">
        <ActivityIndicator label="Loading workspace..." />
      </main>
    );
  if (!me) return <Login onSignedIn={() => void loadMe()} />;
  if (me.user.passwordResetRequired)
    return <ChangePassword onDone={() => void loadMe()} />;
  const displayName = (name: string) =>
    (me.workspaceCount ?? 1) > 1 ? `${name} (${me.tenant.name})` : name;
  const avatarName = displayName(active?.name ?? "Destiny");
  const homeAvatars = me.avatars.filter(
    (a) =>
      a.id === representative?.id ||
      a.avatarKind === "employee" ||
      (!a.mandatory && a.avatarKind !== "consultant"),
  );
  const otherAvatars = homeAvatars.filter((a) => a.id !== activeAvatar);
  const identity = (a: AvatarSummary) => (
    <>
      <h1
        aria-label={displayName(a.name)}
        className="min-w-0 font-heading text-xl font-medium"
      >
        <Button
          variant="link"
          className="h-auto max-w-full justify-start whitespace-normal break-words p-0 text-left text-xl leading-tight"
          aria-haspopup="dialog"
          aria-label={`View ${displayName(a.name)} details`}
          onClick={() => setDetails(a)}
        >
          {displayName(a.name)}
        </Button>
      </h1>
      <p className="mt-1 break-words text-xs leading-relaxed text-muted-foreground">
        {a.id === representative?.id ? "Your Delegate representative" : a.role}
      </p>
    </>
  );
  const placeholder = (name: string) => (
    <div className="flex size-full flex-col items-center justify-center gap-3 p-6 text-center">
      <span className="font-heading text-5xl" aria-hidden="true">
        {name.slice(0, 2)}
      </span>
      <p className="text-xs text-muted-foreground">Portrait not configured</p>
    </div>
  );

  return (
    <main
      className="mx-auto flex h-dvh min-h-0 max-w-6xl flex-col overflow-hidden bg-background"
      aria-label="Delegate workspace"
    >
      <header className="flex shrink-0 justify-end px-4 py-3 sm:px-6">
        <Button
          ref={settingsButton}
          variant="ghost"
          aria-expanded={surface === "settings"}
          onClick={() =>
            surface === "settings" ? closeSurface() : openSurface("settings")
          }
        >
          <Settings /> Settings
        </Button>
      </header>
      {details && (
        <AvatarDetail avatar={details} onClose={() => setDetails(null)} />
      )}
      <ConversationShell
        portraitLayout
        companions={
          otherAvatars.length
            ? otherAvatars.map((a) => (
                <PortraitCard
                  key={a.id}
                  aria-label={displayName(a.name)}
                  identity={identity(a)}
                  portrait={
                    a.id === representative?.id ? (
                      <RestingRepresentative />
                    ) : (
                      placeholder(a.name)
                    )
                  }
                  controls={
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => switchAvatar(a.id)}
                        aria-label={`Enable voice with ${displayName(a.name)}`}
                      >
                        <Phone /> Enable voice
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => switchAvatar(a.id, "text")}
                        aria-label={`Use text with ${displayName(a.name)}`}
                      >
                        <MessageCircle /> Use text
                      </Button>
                    </>
                  }
                />
              ))
            : undefined
        }
        className="flex-1"
        surfaceOpen={surface !== null}
        avatar={
          isRepresentative ? (
            <RepresentativePortrait
              stream={stream}
              status={status}
              onReady={() => setPortraitReady(true)}
              eventSink={eventSink}
            />
          ) : (
            <div className="flex size-full flex-col items-center justify-center gap-3 p-4 text-center">
              <span className="font-heading text-5xl">
                {avatarName.slice(0, 2)}
              </span>
              <p className="text-sm text-muted-foreground">
                Portrait not configured
              </p>
            </div>
          )
        }
        identity={active ? identity(active) : avatarName}
        status={
          <span>
            {statusLabel[status]}
            {muted
              ? " | Microphone muted"
              : textMode && connected
                ? " | Text conversation"
                : ""}
          </span>
        }
        controls={
          <>
            {connected ? (
              <>
                {!textMode && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={muted ? "Unmute microphone" : "Mute microphone"}
                    onClick={() => {
                      clientRef.current?.setMuted(!muted);
                      setMuted(!muted);
                    }}
                  >
                    {muted ? <MicOff /> : <Mic />}
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={stop}>
                  <PhoneOff /> End conversation
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" onClick={() => void start()}>
                <Phone /> Enable voice
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowText(true);
                openSurface("conversation");
              }}
              aria-expanded={showText && surface === "conversation"}
            >
              <MessageCircle /> Use text
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Open transcript"
              aria-expanded={surface === "conversation"}
              onClick={() => openSurface("conversation")}
            >
              <ScrollText />
            </Button>
            {representative && heldTools.includes("notes") && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Open private Notes"
                aria-expanded={surface === "notes"}
                onClick={() => openSurface("notes")}
              >
                <NotebookPen />
              </Button>
            )}
            {heldTools.includes("tasks") && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Open Tasks"
                aria-expanded={surface === "tasks"}
                onClick={() => openSurface("tasks")}
              >
                <ListChecks />
              </Button>
            )}
            {heldTools.includes("calendar") && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Open Calendar"
                aria-expanded={surface === "calendar"}
                onClick={() => openSurface("calendar")}
              >
                <CalendarDays />
              </Button>
            )}
            {error?.includes("paused audio") && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  primeAvatarAudio();
                  void clientRef.current
                    ?.resumeAudio()
                    .then(() => setError(null))
                    .catch(() =>
                      setError(
                        "Audio is still blocked. Check this site's sound permission.",
                      ),
                    );
                }}
              >
                <Volume2 /> Enable audio
              </Button>
            )}
          </>
        }
        surface={
          <div ref={surfaceHost}>
            <WorkSurfaceFrame
              title={surface ? surfaceTitle[surface] : "Settings"}
              onBack={closeSurface}
              description={
                surface === "marketplace"
                  ? "Find the right employee while continuing your conversation."
                  : surface === "notes"
                    ? "Saved in your account, available when you return."
                    : surface === "tasks" || surface === "calendar"
                      ? "Delegate-owned records. Reminders arrive as messages in Delegate."
                      : undefined
              }
            >
              <div hidden={surface !== "settings"}>
                <TenantSettings
                  me={me}
                  activeId={activeAvatar}
                  onSelect={switchAvatar}
                  onOpen={openSurface}
                  onChanged={() => void loadMe()}
                  onSignOut={() => void signOut()}
                >
                  <details className="rounded-lg border border-border p-4">
                    <summary className="cursor-pointer text-sm">
                      Usage and diagnostics
                    </summary>
                    <div className="mt-4 space-y-3">
                      <p className="text-sm">
                        Estimated conversation usage: ${sessionUsd.toFixed(4)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        An estimate, not a spending limit.
                      </p>
                      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all text-xs">
                        {events
                          .map(
                            (e) =>
                              `${e.timestamp} ${e.eventType}${e.toolName ? ` ${e.toolName}` : ""}`,
                          )
                          .join("\n") || "No conversation activity yet."}
                      </pre>
                    </div>
                  </details>
                </TenantSettings>
              </div>
              <div hidden={surface !== "marketplace"}>
                <MarketplaceSurface
                  active={surface === "marketplace"}
                  suggestion={suggestion}
                  onHired={() => void loadMe()}
                />
              </div>
              {representative && (
                <div hidden={surface !== "notes"}>
                  <RepresentativeNotes
                    representativeId={representative.id}
                    detailsEnabled={me.notesDetailsEnabled}
                    active={surface === "notes"}
                    refreshVersion={notesVersion}
                    onEditingChange={onEditingChange}
                  />
                </div>
              )}
              {activeAvatar && heldTools.includes("tasks") && (
                <div hidden={surface !== "tasks"}>
                  <TasksSurface
                    avatarId={activeAvatar}
                    active={surface === "tasks"}
                    refreshVersion={tasksVersion}
                    onEditingChange={onTasksEditing}
                  />
                </div>
              )}
              {activeAvatar && heldTools.includes("calendar") && (
                <div hidden={surface !== "calendar"}>
                  <CalendarSurface
                    avatarId={activeAvatar}
                    active={surface === "calendar"}
                    refreshVersion={calendarVersion}
                    onEditingChange={onCalendarEditing}
                  />
                </div>
              )}
              <div hidden={surface !== "work"}>
                <WorkPanel
                  employeeName={avatarName}
                  state={state}
                  loading={workLoading}
                  error={workError}
                  onRefresh={refreshWork}
                />
              </div>
              <div hidden={surface !== "conversation"} className="space-y-4">
                {transcript.length ? (
                  transcript.map((entry, i) => (
                    <div key={i} className="space-y-1">
                      <Badge variant="outline">
                        {entry.role === "user" ? "You" : avatarName}
                      </Badge>
                      <p className="whitespace-pre-wrap break-words text-sm">
                        {entry.text}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Your current conversation will appear here.
                  </p>
                )}
                {showText && (
                  <form
                    onSubmit={(event) => void send(event)}
                    className="flex gap-2"
                  >
                    <Label htmlFor="conversation-message" className="sr-only">
                      Message your employee
                    </Label>
                    <Input
                      id="conversation-message"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Tell your employee what you need..."
                      maxLength={4000}
                      disabled={
                        sending ||
                        (connected && !!me.communicationsEnabled) ||
                        status === "CONNECTING"
                      }
                    />
                    <Button
                      type="submit"
                      size="icon"
                      aria-label="Send message"
                      disabled={
                        !text.trim() ||
                        sending ||
                        (connected && !!me.communicationsEnabled) ||
                        status === "CONNECTING"
                      }
                    >
                      <Send />
                    </Button>
                  </form>
                )}
              </div>
            </WorkSurfaceFrame>
          </div>
        }
      >
        <div
          className={
            surface ? "space-y-3" : "mx-auto max-w-lg space-y-3 text-center"
          }
        >
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {!activeAvatar && (
            <p className="text-sm text-muted-foreground">
              Open Settings to choose an employee.
            </p>
          )}
        </div>
      </ConversationShell>
    </main>
  );
}
